import React, { useEffect } from "react";
import {
    Activity,
    BarChart3,
    Building2,
    CheckCircle2,
    CreditCard,
    Gauge,
    Search,
    Send,
    User,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import { ROUTES } from "../config/constants";
import { fetchCurrentUser } from "../store/authSlice";
import {
    formatCreditValue,
    getTierLabel,
    getTierName,
} from "../utils/subscription";

const getUsagePercent = (used, limit) => {
    const numericUsed = Number(used || 0);
    const numericLimit = Number(limit || 0);

    if (!numericLimit) return 0;

    return Math.min(100, Math.round((numericUsed / numericLimit) * 100));
};

const hasNumericLimit = (limit) => Number.isFinite(Number(limit));

const ProfileField = ({ label, value, children }) => (
    <div className="min-w-0 rounded-2xl border border-slate-100 bg-[#FAFAFF] p-4 transition hover:border-indigo-100 hover:bg-indigo-50/30">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {label}
        </p>
        <div className="mt-2 min-w-0 break-words text-sm font-semibold leading-6 text-slate-900 sm:text-base">
            {children || value || "N/A"}
        </div>
    </div>
);

const UsageRow = ({
    icon: Icon,
    label,
    value,
    percent,
    showProgress = true,
}) => (
    <div className="flex items-start gap-3 border-b border-slate-100 px-3 py-4 odd:bg-slate-50 even:bg-white last:border-b-0 sm:items-center sm:gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Icon size={18} aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <p className="text-sm font-semibold text-slate-900">{label}</p>
                <p className="text-sm font-medium text-slate-500 sm:shrink-0">
                    {value}
                </p>
            </div>

            {showProgress && (
                <div className="mt-2 h-2 rounded-full bg-slate-100">
                    <div
                        className="h-2 rounded-full bg-indigo-600"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            )}
        </div>
    </div>
);

const Profile = () => {
    const dispatch = useDispatch();
    const { user, workspace, credits, currentUserStatus, currentUserError } =
        useSelector((state) => state.auth);
    const hasContext = useSelector(
        (state) => state.organizationContext.hasContext,
    );
    const navigate = useNavigate();
    const tierName = getTierName(credits, user?.plan);
    const tierLabel = getTierLabel(tierName);
    const displayName = user?.full_name || user?.email || "User";
    const workspaceName =
        workspace?.workspace_name || workspace?.organization_id || "N/A";

    const searchLeadLimit = credits?.search_lead_limit;
    const emailSendLimit = credits?.email_send_limit;

    useEffect(() => {
        dispatch(fetchCurrentUser());
    }, [dispatch]);

    const usageRows = credits
        ? [
              {
                  icon: Search,
                  label: "Search leads usage",
                  value: `${formatCreditValue(
                      credits.search_leads_used,
                      "0",
                  )} / ${formatCreditValue(searchLeadLimit)}`,
                  percent: getUsagePercent(
                      credits.search_leads_used,
                      searchLeadLimit,
                  ),
                  showProgress: hasNumericLimit(searchLeadLimit),
              },
              {
                  icon: BarChart3,
                  label: "Search remaining",
                  value: `${formatCreditValue(
                      credits.search_leads_remaining,
                  )} / ${formatCreditValue(searchLeadLimit)}`,
                  percent: getUsagePercent(
                      credits.search_leads_remaining,
                      searchLeadLimit,
                  ),
                  showProgress: hasNumericLimit(searchLeadLimit),
              },
              {
                  icon: Send,
                  label: "Email sends usage",
                  value: `${formatCreditValue(
                      credits.email_sends_used,
                      "0",
                  )} / ${formatCreditValue(emailSendLimit)}`,
                  percent: getUsagePercent(
                      credits.email_sends_used,
                      emailSendLimit,
                  ),
                  showProgress: hasNumericLimit(emailSendLimit),
              },
          ]
        : [];

    return (
        <div className="min-h-screen bg-[#F8F8FE]">
            <PageContainer className="py-4">
                <header className="mb-5 min-w-0">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                        My Profile
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                        Manage your account information, organization context,
                        and plan usage.
                    </p>
                    {currentUserStatus === "failed" && currentUserError && (
                        <p className="mt-2 max-w-2xl rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                            Unable to refresh profile data: {currentUserError}
                        </p>
                    )}
                </header>

                <div className="grid gap-4 sm:gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)]">
                    <section className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm sm:p-6">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                                <User size={22} aria-hidden="true" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">
                                    Profile Information
                                </h2>
                                <p className="mt-1 text-sm text-slate-500">
                                    View your account and organization details.
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-4">
                            <ProfileField label="Name" value={displayName} />

                            <ProfileField label="Email">
                                <span className="break-all">
                                    {user?.email || "N/A"}
                                </span>
                            </ProfileField>

                            <ProfileField
                                label="Company"
                                value={user?.company_name}
                            />

                            <ProfileField
                                label="Workspace"
                                value={workspaceName}
                            />

                            <ProfileField label="Plan" value={tierLabel} />

                            <ProfileField label="Account Status">
                                <span
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${
                                        user?.is_active
                                            ? "bg-green-50 text-green-700"
                                            : "bg-red-50 text-red-700"
                                    }`}
                                >
                                    {user?.is_active ? (
                                        <CheckCircle2
                                            size={16}
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        <Activity
                                            size={16}
                                            aria-hidden="true"
                                        />
                                    )}
                                    {user?.is_active ? "Active" : "Inactive"}
                                </span>
                            </ProfileField>
                        </div>

                        <div className="mt-5 flex flex-col justify-end gap-3 border-t border-slate-100 pt-5 sm:flex-row">
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(ROUTES.MANAGE_SUBSCRIPTION)
                                }
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-5 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:w-auto"
                            >
                                <CreditCard size={17} aria-hidden="true" />
                                Manage Subscription
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(ROUTES.ORGANIZATION_CONTEXT, {
                                        state: { focusFirstField: true },
                                    })
                                }
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 sm:w-auto"
                            >
                                <Building2 size={17} aria-hidden="true" />
                                {hasContext
                                    ? "Update Organization Context"
                                    : "Create Organization Context"}
                            </button>
                        </div>
                    </section>

                    <section className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm sm:p-6">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                                <Gauge size={22} aria-hidden="true" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">
                                    Plan Usage
                                </h2>
                                <p className="mt-1 text-sm text-slate-500">
                                    Overview of your current plan usage and
                                    limits.
                                </p>
                            </div>
                        </div>

                        {credits ? (
                            <>
                                <div className="mt-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-5">
                                    <div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-indigo-700">
                                                Current Plan
                                            </p>
                                            <p className="mt-2 truncate text-2xl font-bold text-slate-950">
                                                {tierLabel}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 rounded-2xl border border-slate-100 bg-white px-4">
                                    {usageRows.map((row) => (
                                        <UsageRow key={row.label} {...row} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                                Plan usage is not available.
                            </div>
                        )}
                    </section>
                </div>
            </PageContainer>
        </div>
    );
};

export default Profile;
